import React, { useCallback, useMemo, useEffect } from 'react';
import ReactFlow, { Background, useNodesState, useEdgesState, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import './NetworkDiagram.css';

function distributeLayers(numTasks) {
  // Für 7 oder 8 Vorgänge: maximal eine Ebene mit 3, eine mit 2, Rest mit 1
  if (numTasks === 7) {
    const patterns = [
      [1, 2, 1, 3],
      [1, 3, 1, 2],
      [1, 1, 2, 3],
      [1, 2, 3, 1],
      [1, 1, 3, 2],
      [1, 3, 2, 1],
      [1, 2, 2, 2], // auch möglich, falls du das erlauben willst
    ];
    return patterns[Math.floor(Math.random() * patterns.length)];
  } else if (numTasks === 8) {
    const patterns = [
      [1, 2, 2, 3],
      [1, 3, 1, 2, 1],
      [1, 1, 2, 3, 1],
      [1, 2, 3, 2],
      [1, 3, 2, 2],
      [1, 2, 1, 3, 1],
      [1, 1, 3, 2, 1],
      [1, 2, 2, 2, 1],
    ];
    return patterns[Math.floor(Math.random() * patterns.length)];
  } else {
    // Generisch: immer erst 1, dann 2, dann 3, dann wieder 1,2,3 ...
    let left = numTasks;
    let pattern = [1, 2, 3];
    let idx = 0;
    let layers = [];
    while (left > 0) {
      const n = Math.min(pattern[idx % 3], left);
      layers.push(n);
      left -= n;
      idx++;
    }
    return layers;
  }
}

function generateCustomLayeredNetzplan(numTasks = 7) {
  const layerSizes = distributeLayers(numTasks);
  let idChar = 65;
  const layers = [];
  for (let l = 0; l < layerSizes.length; l++) {
    const count = layerSizes[l];
    const prevLayer = l === 0 ? [] : layers[l - 1];
    const thisLayer = [];
    for (let i = 0; i < count; i++) {
      const id = String.fromCharCode(idChar++);
      let dependsOn = [];
      if (l > 0) {
        // 1–Math.min(3, prevLayer.length) Vorgänger
        const numPrevs = Math.min(3, prevLayer.length);
        const shuffled = prevLayer.map(x => x.id).sort(() => 0.5 - Math.random());
        dependsOn = shuffled.slice(0, numPrevs);
      }
      thisLayer.push({
        id,
        name: `Vorgang ${id}`,
        dauer: Math.floor(Math.random() * 12) + 1, // Bis zu 12 Stunden
        dependsOn,
        layer: l
      });
    }
    layers.push(thisLayer);
  }
  return layers.flat();
}

function calculateNetzplan(tasks) {
  const map = Object.fromEntries(tasks.map(t => [t.id, { ...t }]));
  for (const t of tasks) {
    if (t.dependsOn.length === 0) {
      map[t.id].faz = 0;
    } else {
      map[t.id].faz = Math.max(...t.dependsOn.map(pid => map[pid].fez));
    }
    map[t.id].fez = map[t.id].faz + t.dauer;
  }
  const nachfolger = {};
  for (const t of tasks) {
    t.dependsOn.forEach(pid => {
      nachfolger[pid] = nachfolger[pid] || [];
      nachfolger[pid].push(t.id);
    });
  }
  const maxFez = Math.max(...tasks.map(t => map[t.id].fez));
  for (const t of tasks.slice().reverse()) {
    if (!nachfolger[t.id]) {
      map[t.id].sez = maxFez;
    } else {
      map[t.id].sez = Math.min(...nachfolger[t.id].map(nid => map[nid].saz));
    }
    map[t.id].saz = map[t.id].sez - t.dauer;
  }
  for (const t of tasks) {
    map[t.id].gp = map[t.id].saz - map[t.id].faz;
    map[t.id].fp = (nachfolger[t.id] && nachfolger[t.id].length > 0)
      ? Math.min(...nachfolger[t.id].map(nid => map[nid].faz)) - map[t.id].fez
      : map[t.id].sez - map[t.id].fez;
  }
  return Object.values(map);
}

function getNodePositions(tasks) {
  // Gruppiere Knoten nach FAZ (Layer)
  const layers = {};
  tasks.forEach(t => {
    const layer = t.faz || 0;
    if (!layers[layer]) layers[layer] = [];
    layers[layer].push(t);
  });

  // Layout-Parameter
  const MODUL_WIDTH = 180;
  const MODUL_HEIGHT = 120;
  const HORIZONTAL_PADDING = 60;
  const VERTICAL_PADDING = 40;
  const CONTAINER_WIDTH = 1000;
  const CONTAINER_HEIGHT = 500;

  // Spalten (Layer) bestimmen
  const layerKeys = Object.keys(layers).map(Number).sort((a, b) => a - b);
  const numCols = layerKeys.length;
  // const maxRows = Math.max(...layerKeys.map(l => layers[l].length)); // entfernt, da ungenutzt

  // Berechne horizontale Lücke
  const totalWidth = numCols * MODUL_WIDTH + (numCols - 1) * HORIZONTAL_PADDING;
  const xStart = (CONTAINER_WIDTH - totalWidth) / 2;

  const positions = {};
  layerKeys.forEach((layer, colIdx) => {
    const nodesInLayer = layers[layer];
    const n = nodesInLayer.length;
    // Berechne vertikale Lücke für diese Spalte
    const totalHeight = n * MODUL_HEIGHT + (n - 1) * VERTICAL_PADDING;
    const yStart = (CONTAINER_HEIGHT - totalHeight) / 2;
    nodesInLayer.forEach((node, rowIdx) => {
      const x = xStart + colIdx * (MODUL_WIDTH + HORIZONTAL_PADDING);
      const y = yStart + rowIdx * (MODUL_HEIGHT + VERTICAL_PADDING);
      positions[node.id] = { x, y };
    });
  });
  return positions;
}

function NetzplanNode({ data, onInputChange, errors }) {
  // Werte aus Props statt lokalem State
  const inputId = data.valueId || '';
  const inputDauer = data.valueDauer || '';
  const inputGp = data.valueGp || '';
  const inputFp = data.valueFp || '';
  const inputFaz = data.valueFaz || '';
  const inputFez = data.valueFez || '';
  const inputSaz = data.valueSaz || '';
  const inputSez = data.valueSez || '';

  const handleIdChange = (e) => {
    const val = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 1);
    if (onInputChange) onInputChange(data.id, 'id', val);
  };
  const handleDauerChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'dauer', val);
  };
  const handleGpChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'gp', val);
  };
  const handleFpChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'fp', val);
  };
  const handleFazChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'faz', val);
  };
  const handleFezChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'fez', val);
  };
  const handleSazChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'saz', val);
  };
  const handleSezChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (onInputChange) onInputChange(data.id, 'sez', val);
  };
  return (
    <div className={`netzplan-node${errors && errors.module ? ' netzplan-module-error' : ''}`}>
      <div className="netzplan-times">
        <div className="netzplan-faz">
          <input
            className={`netzplan-faz-input${inputFaz ? ' netzplan-faz-transparent' : ''}${errors && errors.faz ? ' netzplan-faz-error' : ''}`}
            value={inputFaz}
            onChange={handleFazChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <div className="netzplan-fez">
          <input
            className={`netzplan-faz-input${inputFez ? ' netzplan-faz-transparent' : ''}${errors && errors.fez ? ' netzplan-faz-error' : ''}`}
            value={inputFez}
            onChange={handleFezChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <div className="netzplan-saz">
          <input
            className={`netzplan-faz-input${inputSaz ? ' netzplan-faz-transparent' : ''}${errors && errors.saz ? ' netzplan-faz-error' : ''}`}
            value={inputSaz}
            onChange={handleSazChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <div className="netzplan-sez">
          <input
            className={`netzplan-faz-input${inputSez ? ' netzplan-faz-transparent' : ''}${errors && errors.sez ? ' netzplan-faz-error' : ''}`}
            value={inputSez}
            onChange={handleSezChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </div>
      <div className="module-grid">
        <div className="grid-cell vorgangsnummer">
          <input
            className={`netzplan-id-input netzplan-id-transparent${errors && errors.id ? ' netzplan-id-error' : ''}`}
            value={inputId}
            onChange={handleIdChange}
            maxLength={1}
            autoComplete="off"
          />
        </div>
        <div className="grid-cell vorgangsname"></div>
        <div className="grid-cell dauer">
          <input
            className={`netzplan-dauer-input${errors && errors.dauer ? ' netzplan-dauer-error' : ''}`}
            value={inputDauer}
            onChange={handleDauerChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <div className="grid-cell gp">
          <input
            className={`netzplan-dauer-input${errors && errors.gp ? ' netzplan-dauer-error' : ''}`}
            value={inputGp}
            onChange={handleGpChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
        <div className="grid-cell fp">
          <input
            className={`netzplan-dauer-input${errors && errors.fp ? ' netzplan-dauer-error' : ''}`}
            value={inputFp}
            onChange={handleFpChange}
            maxLength={3}
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </div>
      <Handle type="target" position={Position.Left} style={{ background: '#888' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#888' }} />
    </div>
  );
}

const nodeTypes = { netzplan: (nodeProps) => {
  // Fehler für diesen Knoten extrahieren
  const { errors, onInputChange } = nodeProps.data;
  return <NetzplanNode {...nodeProps} errors={errors} onInputChange={onInputChange} />;
}};

function fixNetzplanDependencies(tasks) {
  // 1. Gruppiere Vorgänge nach Ebene
  let changed = true;
  while (changed) {
    changed = false;
    const layers = {};
    for (const t of tasks) {
      if (!layers[t.layer]) layers[t.layer] = [];
      layers[t.layer].push(t);
    }
    // Prüfe jede Ebene auf Überbelegung
    for (const layerNum of Object.keys(layers).map(Number).sort((a, b) => a - b)) {
      const layer = layers[layerNum];
      if (layer.length > 3) {
        // Verschiebe überzählige Vorgänge in die nächste Ebene
        for (let i = 3; i < layer.length; i++) {
          layer[i].layer = layerNum + 1;
          changed = true;
        }
      }
    }
  }
  // 2. Abhängigkeiten neu setzen: Nur Vorgänger aus layer-1, max. 3 pro Vorgang
  for (const t of tasks) {
    if (t.layer === 0) {
      t.dependsOn = [];
    } else {
      const prevLayerTasks = tasks.filter(x => x.layer === t.layer - 1);
      const prevLayerIds = prevLayerTasks.map(x => x.id);
      // Wähle 1 bis 3 Vorgänger zufällig aus der vorherigen Ebene
      const numPrevs = Math.min(3, prevLayerIds.length);
      const shuffled = prevLayerIds.sort(() => 0.5 - Math.random());
      t.dependsOn = shuffled.slice(0, numPrevs);
    }
  }
  // 3. Sicherstellen, dass es nur einen Startvorgang gibt
  let startTasks = tasks.filter(t => t.dependsOn.length === 0);
  if (startTasks.length > 1) {
    // Wähle den ersten als echten Startvorgang
    const mainStart = startTasks[0];
    for (let i = 1; i < startTasks.length; i++) {
      startTasks[i].dependsOn = [mainStart.id];
      startTasks[i].layer = 1;
    }
  }
  // 4. Nachkorrektur: Alle Abhängigkeiten erneut prüfen (nur aus layer-1, max. 3)
  for (const t of tasks) {
    if (t.layer === 0) {
      t.dependsOn = [];
    } else {
      const prevLayerTasks = tasks.filter(x => x.layer === t.layer - 1);
      const prevLayerIds = prevLayerTasks.map(x => x.id);
      const numPrevs = Math.min(3, prevLayerIds.length);
      const shuffled = prevLayerIds.sort(() => 0.5 - Math.random());
      t.dependsOn = shuffled.slice(0, numPrevs);
    }
  }
  return tasks;
}

export { generateCustomLayeredNetzplan, fixNetzplanDependencies, calculateNetzplan };

export default function NetworkDiagram({ tasks: externalTasks, onInputChange, errors, inputValues }) {
  // Wenn tasks als Prop übergeben werden, verwende diese, sonst generiere selbst
  const tasks = useMemo(() => {
    if (externalTasks && Array.isArray(externalTasks)) {
      return externalTasks;
    }
    const key = 'netzplan_lernfeld4';
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Fallback falls kaputt
      }
    }
    let generated = generateCustomLayeredNetzplan(7);
    generated = fixNetzplanDependencies(generated);
    localStorage.setItem(key, JSON.stringify(generated));
    return generated;
  }, [externalTasks]);

  const netzplan = useMemo(() => calculateNetzplan(tasks), [tasks]);
  const positions = useMemo(() => getNodePositions(netzplan), [netzplan]);
  
  // Debug-Ausgabe für Netzplan und Positionen
  console.log('=== NETZPLAN DEBUG ===');
  console.log('Tasks (Original):', tasks);
  console.log('Netzplan (Berechnet):', netzplan);
  console.log('Positionen:', positions);
  console.log('=== ENDE DEBUG ===');
  const nodes = useMemo(() => netzplan.map(m => {
    const values = inputValues && inputValues[m.id] ? inputValues[m.id] : {};
    return {
      id: m.id,
      type: 'netzplan',
      position: positions[m.id],
      data: {
        ...m,
        onInputChange,
        errors: errors ? errors[m.id] : undefined,
        valueId: values.id,
        valueDauer: values.dauer,
        valueGp: values.gp,
        valueFp: values.fp,
        valueFaz: values.faz,
        valueFez: values.fez,
        valueSaz: values.saz,
        valueSez: values.sez,
      },
    };
  }), [netzplan, positions, onInputChange, errors, inputValues]);
  const edges = useMemo(() => {
    const e = [];
    netzplan.forEach(target => {
      target.dependsOn.forEach(sourceId => {
        e.push({ id: `${sourceId}->${target.id}`, source: sourceId, target: target.id, animated: true });
      });
    });
    return e;
  }, [netzplan]);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState(nodes);

  useEffect(() => {
    setRfNodes(nodes);
  }, [nodes, setRfNodes]);

  const [rfEdges, , onEdgesChange] = useEdgesState(edges);

  const onInit = useCallback((instance) => {
    if (instance.fitView) instance.fitView();
  }, []);

  return (
    <div>
      <div style={{ width: '100%', height: 500 }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnScroll={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          selectionOnDrag={false}
          draggable={false}
          preventScrolling={false}
          onInit={onInit}
        >
          <Background color="#eee" gap={16} />
        </ReactFlow>
      </div>
    </div>
  );
} 